import React, { useState, useEffect } from 'react';
import { Container, Card, Alert, Form, Button, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchCommunities, joinCommunity } from '../../services/communityService';

function JoinCommunity() {
  const [communities, setCommunities] = useState([]);
  const [userCommunitiesIds, setUserCommunitiesIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [makePrimary, setMakePrimary] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const navigate = useNavigate();
  const { userCommunities, refreshCsrfToken } = useAuth();
  const { t } = useLanguage();
  
  useEffect(() => {
    // Create a set of community IDs the user is already a member of
    if (userCommunities) {
      setUserCommunitiesIds(new Set(userCommunities.map(c => c.id)));
    }
    
    async function loadCommunities() {
      try {
        setLoading(true);
        setError('');
        
        const response = await fetchCommunities();
        if (response && response.communities) {
          setCommunities(response.communities);
        }
      } catch (err) {
        setError(t('communities.errors.fetchError'));
      } finally {
        setLoading(false);
      }
    }

    loadCommunities();
  }, [userCommunities, t]);
  
  const handleCommunitySelect = (community) => {
    setSelectedCommunity(community);
  };
  
  const handleJoinCommunity = async () => {
    if (!selectedCommunity) return;
    
    setIsJoining(true);
    setError('');
    setSuccessMessage('');
    
    try {
      // Refresh CSRF token before making the request
      await refreshCsrfToken();
      
      // Join the community
      const response = await joinCommunity(selectedCommunity.id, makePrimary);

      setSuccessMessage(t('communities.joinedSuccess', { name: selectedCommunity.name }));

      // Update the list of user's communities
      setUserCommunitiesIds(prev => new Set([...prev, selectedCommunity.id]));

      // Reset selection
      setSelectedCommunity(null);

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Ensure AuthContext is updated
      }, 2000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        t('communities.errors.joinError')
      );
    } finally {
      setIsJoining(false);
    }
  };
  
  // Filter communities based on search term
  const filteredCommunities = searchTerm
    ? communities.filter(c => 
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.description && c.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : communities;
  
  // Filter out communities the user is already a member of
  const availableCommunities = filteredCommunities.filter(
    c => !userCommunitiesIds.has(c.id)
  );

  return (
    <Container className="py-5">
      <Card className="shadow border-0 rounded-4">
        <Card.Body className="p-4">
          <h2 className="text-center mb-4">{t('communities.joinCommunity')}</h2>

          {successMessage && (
            <Alert variant="success">{successMessage}</Alert>
          )}

          {error && (
            <Alert variant="danger">{error}</Alert>
          )}

          {!loading && availableCommunities.length === 0 && (
            <Alert variant="info">
              {t('communities.noAvailableCommunities')}
              {userCommunities && userCommunities.length > 0 && (
                <span> {t('communities.alreadyMemberOfAll')}</span>
              )}
            </Alert>
          )}

          {loading ? (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t('common.loading')}</span>
              </div>
              <p className="mt-2">{t('communities.loading')}</p>
            </div>
          ) : (
            <>
              {availableCommunities.length > 0 && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('communities.searchCommunities')}</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={t('communities.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>

                  <div className="mb-4">
                    <h5>{t('communities.availableCommunities')}</h5>
                    <p className="text-muted small">{t('communities.selectCommunityToJoin')}</p>
                    
                    <ListGroup>
                      {availableCommunities.map(community => (
                        <ListGroup.Item 
                          key={community.id}
                          action
                          active={selectedCommunity && selectedCommunity.id === community.id}
                          onClick={() => handleCommunitySelect(community)}
                          className="d-flex flex-column align-items-start"
                        >
                          <div className="d-flex w-100 justify-content-between">
                            <h6 className="mb-1">{community.name}</h6>
                          </div>
                          {community.description && (
                            <p className="mb-1 small text-muted">
                              {community.description.substring(0, 100)}
                              {community.description.length > 100 ? '...' : ''}
                            </p>
                          )}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                  
                  {selectedCommunity && (
                    <div className="mb-4">
                      <h5>{t('communities.selectedCommunity')}</h5>
                      <Card className="bg-light">
                        <Card.Body>
                          <Card.Title>{selectedCommunity.name}</Card.Title>
                          <Card.Text>
                            {selectedCommunity.description || t('communities.noDescription')}
                          </Card.Text>

                          {userCommunities && userCommunities.length > 0 && (
                            <Form.Check
                              type="checkbox"
                              id="make-primary"
                              label={t('communities.setPrimary')}
                              checked={makePrimary}
                              onChange={(e) => setMakePrimary(e.target.checked)}
                              className="mb-3"
                            />
                          )}

                          <Button
                            variant="primary"
                            onClick={handleJoinCommunity}
                            disabled={isJoining}
                          >
                            {isJoining ? t('communities.joining') : t('communities.join')}
                          </Button>
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </>
              )}
              
              <div className="mt-4 d-flex justify-content-between">
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(-1)}
                >
                  {t('navigation.back')}
                </Button>

              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default JoinCommunity;